import { useState, useCallback, useRef } from "react";

// ─── Rule Engine ────────────────────────────────────────────────────────────

const OPERATORS = {
  contains: (val, arg) => String(val).toLowerCase().includes(String(arg).toLowerCase()),
  not_contains: (val, arg) => !String(val).toLowerCase().includes(String(arg).toLowerCase()),
  starts_with: (val, arg) => String(val).toLowerCase().startsWith(String(arg).toLowerCase()),
  ends_with: (val, arg) => String(val).toLowerCase().endsWith(String(arg).toLowerCase()),
  equals: (val, arg) => String(val).toLowerCase() === String(arg).toLowerCase(),
  not_equals: (val, arg) => String(val).toLowerCase() !== String(arg).toLowerCase(),
  regex: (val, arg) => { try { return new RegExp(arg).test(val); } catch { return false; } },
  gt: (val, arg) => parseFloat(val) > parseFloat(arg),
  lt: (val, arg) => parseFloat(val) < parseFloat(arg),
};

const EXTRACTORS = {
  regex_group: (text, pattern, group = 1) => {
    try {
      const match = text.match(new RegExp(pattern));
      return match ? match[parseInt(group)] || match[0] : null;
    } catch { return null; }
  },
  regex_all: (text, pattern) => {
    try {
      return [...text.matchAll(new RegExp(pattern, "g"))].map(m => m[0]).join(", ") || null;
    } catch { return null; }
  },
  between: (text, start, end) => {
    const s = text.indexOf(start);
    if (s === -1) return null;
    const e = text.indexOf(end, s + start.length);
    return e === -1 ? null : text.slice(s + start.length, e).trim();
  },
  after: (text, delimiter) => {
    const i = text.indexOf(delimiter);
    return i === -1 ? null : text.slice(i + delimiter.length).trim();
  },
  before: (text, delimiter) => {
    const i = text.indexOf(delimiter);
    return i === -1 ? null : text.slice(0, i).trim();
  },
  nth_word: (text, n) => text.trim().split(/\s+/)[parseInt(n) - 1] || null,
  json_path: (text, path) => {
    try {
      let obj = JSON.parse(text);
      return path.split(".").reduce((o, k) => o?.[k], obj) ?? null;
    } catch { return null; }
  },
  trim: (text) => text.trim() || null,
};

function applyRule(rule, text) {
  const extractor = EXTRACTORS[rule.extractorType];
  if (!extractor) return null;
  const args = [text, rule.arg1, rule.arg2, rule.arg3].filter(a => a !== undefined && a !== "");
  return extractor(...args);
}

function evaluateCondition(cond, text) {
  const op = OPERATORS[cond.operator];
  if (!op) return true;
  return op(text, cond.value);
}

function runExtractionRules(rules, input) {
  const results = [];
  for (const rule of rules) {
    if (!rule.enabled) continue;
    const conditionMet = rule.conditions.length === 0 || rule.conditions.every(c => evaluateCondition(c, input));
    if (!conditionMet) {
      results.push({ rule, value: null, status: "skipped", reason: "Condition not met" });
      continue;
    }
    const value = applyRule(rule, input);
    results.push({ rule, value, status: value !== null ? "success" : "no_match" });
  }
  return results;
}

// ─── Default Sample Rules ────────────────────────────────────────────────────

const DEFAULT_RULES = [
  {
    id: "r1", name: "Extract Email", enabled: true,
    extractorType: "regex_group", arg1: "[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}", arg2: "0", arg3: "",
    conditions: [], fieldName: "email",
  },
  {
    id: "r2", name: "Extract Order ID", enabled: true,
    extractorType: "between", arg1: "Order #", arg2: " ", arg3: "",
    conditions: [{ id: "c1", operator: "contains", value: "Order" }],
    fieldName: "order_id",
  },
  {
    id: "r3", name: "Extract Dollar Amount", enabled: true,
    extractorType: "regex_group", arg1: "\\$([\\d,]+\\.?\\d*)", arg2: "1", arg3: "",
    conditions: [], fieldName: "amount",
  },
  {
    id: "r4", name: "Extract Date", enabled: true,
    extractorType: "regex_group", arg1: "\\d{4}-\\d{2}-\\d{2}", arg2: "0", arg3: "",
    conditions: [], fieldName: "date",
  },
];

const SAMPLE_INPUTS = [
  "Order #78234 placed on 2024-03-15 for $1,299.00. Confirmation sent to john.doe@example.com",
  "Invoice from acme@corp.io dated 2024-01-22. Total: $450.50",
  "No order here. Just a plain text message.",
  `{"user": {"email": "api@test.com", "id": 42}}`,
];

// ─── Components ──────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 8);

const EXTRACTOR_DEFS = [
  { value: "regex_group", label: "Regex Group", args: ["Pattern", "Group #"] },
  { value: "regex_all", label: "Regex All Matches", args: ["Pattern"] },
  { value: "between", label: "Between Delimiters", args: ["Start", "End"] },
  { value: "after", label: "After Delimiter", args: ["Delimiter"] },
  { value: "before", label: "Before Delimiter", args: ["Delimiter"] },
  { value: "nth_word", label: "Nth Word", args: ["N"] },
  { value: "json_path", label: "JSON Path", args: ["Path (dot notation)"] },
  { value: "trim", label: "Trim Whitespace", args: [] },
];

const OPERATOR_LABELS = {
  contains: "contains", not_contains: "does not contain", starts_with: "starts with",
  ends_with: "ends with", equals: "equals", not_equals: "not equals",
  regex: "matches regex", gt: ">", lt: "<",
};

function RuleCard({ rule, onUpdate, onDelete, onTest, testResult }) {
  const [expanded, setExpanded] = useState(false);
  const extDef = EXTRACTOR_DEFS.find(e => e.value === rule.extractorType) || EXTRACTOR_DEFS[0];
  const argLabels = extDef.args;

  const update = (key, val) => onUpdate({ ...rule, [key]: val });
  const updateArg = (key, val) => onUpdate({ ...rule, [key]: val });

  const statusColor = !testResult ? "#555" :
    testResult.status === "success" ? "#00e5a0" :
    testResult.status === "skipped" ? "#f5a623" : "#ff4d4d";

  return (
    <div style={{
      background: "linear-gradient(135deg, #0d1117 0%, #161b22 100%)",
      border: `1px solid ${expanded ? "#30363d" : "#21262d"}`,
      borderLeft: `3px solid ${rule.enabled ? "#238636" : "#30363d"}`,
      borderRadius: "8px",
      marginBottom: "10px",
      transition: "all 0.2s",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", gap: "10px", cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}>
        <button onClick={e => { e.stopPropagation(); update("enabled", !rule.enabled); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
          <div style={{
            width: 32, height: 18, borderRadius: 9,
            background: rule.enabled ? "#238636" : "#21262d",
            position: "relative", transition: "background 0.2s",
          }}>
            <div style={{
              position: "absolute", top: 3, left: rule.enabled ? 17 : 3,
              width: 12, height: 12, borderRadius: "50%",
              background: "#fff", transition: "left 0.2s",
            }} />
          </div>
        </button>

        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#8b949e", minWidth: 80 }}>
          {rule.fieldName || "field"}
        </span>
        <span style={{ flex: 1, color: "#e6edf3", fontSize: 14, fontWeight: 500 }}>{rule.name}</span>
        <span style={{
          fontSize: 10, padding: "2px 8px", borderRadius: 12,
          background: "#161b22", color: "#7d8590", border: "1px solid #21262d",
          fontFamily: "monospace",
        }}>{extDef.label}</span>

        {testResult && (
          <span style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 12,
            color: statusColor, border: `1px solid ${statusColor}33`,
            background: `${statusColor}11`,
          }}>
            {testResult.status === "success" ? `✓ ${String(testResult.value).slice(0, 20)}` :
             testResult.status === "skipped" ? "⊘ skipped" : "✗ no match"}
          </span>
        )}

        <span style={{ color: "#8b949e", fontSize: 12 }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded Config */}
      {expanded && (
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid #21262d" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
            <div>
              <label style={labelStyle}>Rule Name</label>
              <input style={inputStyle} value={rule.name} onChange={e => update("name", e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Output Field Name</label>
              <input style={inputStyle} value={rule.fieldName} onChange={e => update("fieldName", e.target.value)} placeholder="e.g. email, amount" />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>Extractor Type</label>
            <select style={inputStyle} value={rule.extractorType}
              onChange={e => update("extractorType", e.target.value)}>
              {EXTRACTOR_DEFS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>

          {argLabels.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${argLabels.length}, 1fr)`, gap: 10, marginTop: 10 }}>
              {argLabels.map((label, i) => {
                const argKey = `arg${i + 1}`;
                return (
                  <div key={label}>
                    <label style={labelStyle}>{label}</label>
                    <input style={inputStyle} value={rule[argKey] || ""}
                      onChange={e => updateArg(argKey, e.target.value)} placeholder={label} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Conditions */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Conditions (ALL must match)</label>
              <button onClick={() => update("conditions", [...rule.conditions, { id: uid(), operator: "contains", value: "" }])}
                style={smallBtnStyle}>+ Add Condition</button>
            </div>
            {rule.conditions.map((cond, i) => (
              <div key={cond.id} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                <select style={{ ...inputStyle, flex: 1 }} value={cond.operator}
                  onChange={e => {
                    const updated = [...rule.conditions];
                    updated[i] = { ...cond, operator: e.target.value };
                    update("conditions", updated);
                  }}>
                  {Object.entries(OPERATOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input style={{ ...inputStyle, flex: 2 }} placeholder="Value" value={cond.value}
                  onChange={e => {
                    const updated = [...rule.conditions];
                    updated[i] = { ...cond, value: e.target.value };
                    update("conditions", updated);
                  }} />
                <button onClick={() => update("conditions", rule.conditions.filter((_, j) => j !== i))}
                  style={{ ...smallBtnStyle, color: "#ff4d4d", borderColor: "#ff4d4d33", background: "#ff4d4d11" }}>✕</button>
              </div>
            ))}
            {rule.conditions.length === 0 && (
              <p style={{ color: "#8b949e", fontSize: 11, fontStyle: "italic", margin: 0 }}>No conditions — rule always runs</p>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={onTest} style={{ ...smallBtnStyle, color: "#58a6ff", borderColor: "#58a6ff33", background: "#58a6ff11" }}>
              ▶ Test Rule
            </button>
            <button onClick={onDelete} style={{ ...smallBtnStyle, color: "#ff4d4d", borderColor: "#ff4d4d33", background: "#ff4d4d11", marginLeft: "auto" }}>
              Delete Rule
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 11, color: "#8b949e", marginBottom: 4, letterSpacing: "0.05em", textTransform: "uppercase" };
const inputStyle = {
  width: "100%", background: "#0d1117", border: "1px solid #30363d",
  borderRadius: 6, color: "#e6edf3", padding: "7px 10px", fontSize: 13,
  fontFamily: "'JetBrains Mono', monospace", outline: "none", boxSizing: "border-box",
};
const smallBtnStyle = {
  background: "#21262d11", border: "1px solid #30363d", borderRadius: 6,
  color: "#8b949e", padding: "5px 12px", fontSize: 12, cursor: "pointer",
  fontFamily: "inherit", transition: "all 0.15s",
};

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function ExtractionEngine() {
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [input, setInput] = useState(SAMPLE_INPUTS[0]);
  const [results, setResults] = useState([]);
  const [hasRun, setHasRun] = useState(false);
  const [activeTab, setActiveTab] = useState("rules");

  const updateRule = (id, updated) => setRules(rules.map(r => r.id === id ? updated : r));
  const deleteRule = id => setRules(rules.filter(r => r.id !== id));
  const addRule = () => setRules([...rules, {
    id: uid(), name: "New Rule", enabled: true,
    extractorType: "regex_group", arg1: "", arg2: "0", arg3: "",
    conditions: [], fieldName: "field_" + uid(),
  }]);

  const runAll = useCallback(() => {
    setResults(runExtractionRules(rules, input));
    setHasRun(true);
    setActiveTab("results");
  }, [rules, input]);

  const testSingle = (rule) => {
    const r = runExtractionRules([rule], input);
    setResults(prev => {
      const existing = prev.filter(x => x.rule.id !== rule.id);
      return [...existing, ...r];
    });
    setHasRun(true);
  };

  const resultMap = Object.fromEntries(results.map(r => [r.rule.id, r]));
  const successCount = results.filter(r => r.status === "success").length;
  const extractedObj = Object.fromEntries(
    results.filter(r => r.status === "success").map(r => [r.rule.fieldName, r.value])
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: "#010409",
      color: "#e6edf3",
      fontFamily: "'Inter', sans-serif",
      padding: "32px 24px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        select, input, textarea { transition: border-color 0.15s; }
        select:focus, input:focus, textarea:focus { border-color: #388bfd !important; outline: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #0d1117; }
        ::-webkit-scrollbar-thumb { background: #30363d; border-radius: 2px; }
        button:hover { opacity: 0.85; }
      `}</style>

      {/* Header */}
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#238636", boxShadow: "0 0 8px #238636" }} />
              <span style={{ fontSize: 11, letterSpacing: "0.15em", color: "#8b949e", textTransform: "uppercase" }}>Enterprise Data Extraction</span>
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", color: "#f0f6fc" }}>
              Rule-Based Extraction Engine
            </h1>
            <p style={{ margin: "6px 0 0", color: "#8b949e", fontSize: 14 }}>
              Define conditional extraction rules — regex, delimiters, JSON paths, and more
            </p>
          </div>
          <button onClick={runAll} style={{
            background: "linear-gradient(135deg, #238636, #2ea043)",
            border: "none", borderRadius: 8, color: "#fff",
            padding: "12px 24px", fontSize: 14, fontWeight: 600,
            cursor: "pointer", letterSpacing: "0.02em",
            boxShadow: "0 4px 16px #23863644",
          }}>
            ▶ Run All Rules
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20 }}>
          {/* Left Column */}
          <div>
            {/* Input Panel */}
            <div style={{
              background: "#0d1117", border: "1px solid #21262d",
              borderRadius: 10, marginBottom: 20, overflow: "hidden",
            }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #21262d", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#e6edf3" }}>Input Text</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {SAMPLE_INPUTS.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} style={{
                      ...smallBtnStyle, fontSize: 11, padding: "3px 8px",
                      ...(input === s ? { color: "#58a6ff", borderColor: "#58a6ff44", background: "#58a6ff11" } : {}),
                    }}>Sample {i + 1}</button>
                  ))}
                </div>
              </div>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                style={{
                  width: "100%", minHeight: 100, background: "transparent",
                  border: "none", color: "#e6edf3", padding: "14px 16px",
                  fontSize: 13, fontFamily: "'JetBrains Mono', monospace",
                  resize: "vertical", outline: "none", lineHeight: 1.6,
                }}
              />
            </div>

            {/* Rules / Results Tabs */}
            <div style={{ display: "flex", gap: 2, marginBottom: 16 }}>
              {["rules", "results"].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                  background: activeTab === tab ? "#161b22" : "transparent",
                  border: `1px solid ${activeTab === tab ? "#30363d" : "transparent"}`,
                  borderRadius: 6, color: activeTab === tab ? "#e6edf3" : "#8b949e",
                  padding: "8px 16px", fontSize: 13, cursor: "pointer",
                  fontWeight: activeTab === tab ? 500 : 400,
                }}>
                  {tab === "rules" ? `Rules (${rules.length})` : `Results${hasRun ? ` (${successCount}/${results.length})` : ""}`}
                </button>
              ))}
            </div>

            {activeTab === "rules" && (
              <>
                {rules.map(rule => (
                  <RuleCard key={rule.id} rule={rule}
                    onUpdate={updated => updateRule(rule.id, updated)}
                    onDelete={() => deleteRule(rule.id)}
                    onTest={() => testSingle(rule)}
                    testResult={resultMap[rule.id]}
                  />
                ))}
                <button onClick={addRule} style={{
                  width: "100%", background: "transparent",
                  border: "1px dashed #30363d", borderRadius: 8,
                  color: "#8b949e", padding: "12px", fontSize: 13,
                  cursor: "pointer", marginTop: 4,
                }}>
                  + Add Extraction Rule
                </button>
              </>
            )}

            {activeTab === "results" && (
              <div>
                {!hasRun && (
                  <div style={{ textAlign: "center", color: "#8b949e", padding: 40, fontSize: 13 }}>
                    Run the engine to see results
                  </div>
                )}
                {results.map((r, i) => (
                  <div key={i} style={{
                    background: "#0d1117", border: "1px solid #21262d",
                    borderRadius: 8, padding: "12px 16px", marginBottom: 8,
                    borderLeft: `3px solid ${r.status === "success" ? "#238636" : r.status === "skipped" ? "#f5a623" : "#f85149"}`,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: "#e6edf3" }}>{r.rule.name}</span>
                        <span style={{ fontSize: 11, color: "#8b949e", marginLeft: 8 }}>→ {r.rule.fieldName}</span>
                      </div>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10,
                        color: r.status === "success" ? "#3fb950" : r.status === "skipped" ? "#f5a623" : "#f85149",
                        background: r.status === "success" ? "#3fb95011" : r.status === "skipped" ? "#f5a62311" : "#f8514911",
                      }}>
                        {r.status}
                      </span>
                    </div>
                    {r.status === "success" && (
                      <div style={{ marginTop: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#79c0ff" }}>
                        "{r.value}"
                      </div>
                    )}
                    {r.reason && <div style={{ marginTop: 4, fontSize: 11, color: "#8b949e" }}>{r.reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel */}
          <div>
            {/* Stats */}
            <div style={{
              background: "#0d1117", border: "1px solid #21262d",
              borderRadius: 10, padding: 16, marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>Pipeline Stats</div>
              {[
                ["Total Rules", rules.length],
                ["Active Rules", rules.filter(r => r.enabled).length],
                ["Successful", results.filter(r => r.status === "success").length],
                ["Skipped", results.filter(r => r.status === "skipped").length],
                ["No Match", results.filter(r => r.status === "no_match").length],
              ].map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: "#8b949e" }}>{label}</span>
                  <span style={{ color: "#e6edf3", fontFamily: "monospace", fontWeight: 500 }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Extracted JSON */}
            <div style={{
              background: "#0d1117", border: "1px solid #21262d",
              borderRadius: 10, overflow: "hidden", marginBottom: 16,
            }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #21262d", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Extracted Payload</span>
                <button onClick={() => navigator.clipboard?.writeText(JSON.stringify(extractedObj, null, 2))}
                  style={{ ...smallBtnStyle, fontSize: 11, padding: "3px 10px" }}>Copy JSON</button>
              </div>
              <pre style={{
                margin: 0, padding: 16, fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                color: Object.keys(extractedObj).length ? "#79c0ff" : "#8b949e",
                lineHeight: 1.6, overflowX: "auto",
              }}>
                {Object.keys(extractedObj).length
                  ? JSON.stringify(extractedObj, null, 2)
                  : "// Run engine to see\n// extracted fields here"}
              </pre>
            </div>

            {/* Extractor Reference */}
            <div style={{
              background: "#0d1117", border: "1px solid #21262d",
              borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>Extractor Reference</div>
              {EXTRACTOR_DEFS.map(d => (
                <div key={d.value} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#e6edf3", fontWeight: 500 }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: "#8b949e", fontFamily: "monospace" }}>
                    args: [{d.args.join(", ") || "none"}]
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
